import React, { useEffect, useState } from 'react';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Alert,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { TableSkeleton } from '../components/shared/skeletons';
import { useAuditsController } from './context/audits-controller';
import { useAuditsState } from './context/audits-context';
import { IAuditResponse } from '../../model/base/response/audit-response';
import { getAuditStatusLabel, getAuditStatusColor } from 'utils';

export function AuditsComponent() {
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadAudits } = useAuditsController();
  const { audits, loading, error } = useAuditsState();

  const [filters, setFilters] = useState({
    term: '',
    year: undefined,
    statusId: undefined,
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    loadAudits(filters);
  }, [filters]);

  const filterFields: FilterField[] = [
    {
      name: 'term',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por unidade ou processo',
      gridSize: { xs: 12, md: 8 },
    },
    {
      name: 'statusId',
      label: 'Situação',
      type: 'select',
      options: [
        { value: 1, label: 'Entregue' },
        { value: 2, label: 'Sobrestado' },
        { value: 3, label: 'Julgado' },
      ],
      placeholder: 'Selecione a situação',
      gridSize: { xs: 12, md: 4 },
    },
  ];

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 0,
    }));
  };

  const handleSearch = () => {
    loadAudits(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      term: '',
      year: undefined,
      statusId: undefined,
      page: 0,
      pageSize: 10,
    };
    setFilters(clearedFilters);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleViewDetails = (id: number) => {
    navigate(`/auditorias/detalhes/${id}`);
  };

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title="Auditorias"
          subtitle="Consulte as auditorias realizadas no município"
          breadcrumbs={[{ label: 'Auditorias' }]}
        />

        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof typeof filters],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
        />

        {loading ? (
          <TableSkeleton rows={10} columns={5} hasActions />
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : audits.items.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhuma auditoria encontrada.
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper} elevation={3} sx={{ mt: 3, borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Unidade</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Processo</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Ano</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Situação</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audits.items.map((audit: IAuditResponse) => (
                    <TableRow
                      key={audit.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <TableCell>{audit.unitName || '-'}</TableCell>
                      <TableCell>{audit.processNumber || '-'}</TableCell>
                      <TableCell>{audit.year || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={getAuditStatusLabel(audit.status)}
                          color={getAuditStatusColor(audit.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver Detalhes" arrow>
                          <IconButton
                            color="primary"
                            onClick={() => handleViewDetails(audit.id)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                              },
                            }}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {audits.pageCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 3 }}>
                <Pagination
                  count={audits.pageCount}
                  page={audits.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </PageContainer>
  );
}
